import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { Lead, KanbanData, LeadOwnershipCounts, LeadOwnershipCountsFilters } from '../types/crm.types';
import type { LeadFormData } from '../validators/leadSchema';

export interface LeadFilters {
  pipeline_id?: string;
  stage_id?: string;
  assigned_to?: string;
}

export const leadApi = {
  // Get all leads with filters (paginated)
  getLeads: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    stage_id?: string;
    pipeline_id?: string;
    assigned_to?: string;
    customer_lifecycle_stage?: 'lead' | 'customer' | 'former_customer';
    sort_by?: string;
    sort_order?: string;
    deleted?: 'only' | 'with';
  }) => tenantApi.getPaginated<Lead>('/leads', { params }),

  // Get single lead
  getLead: (id: string, params?: { include_deleted?: boolean }) =>
    tenantApi.getOne<Lead>(`/leads/${id}`, { params }),

  /** Create new lead */
  createLead: (leadData: LeadFormData) =>
    tenantApi.postOne<Lead>('/leads', leadData),

  /** Update existing lead */
  updateLead: (id: string, leadData: Partial<LeadFormData>) =>
    tenantApi.putOne<Lead>(`/leads/${id}`, leadData),

  /** Delete lead (soft delete) */
  deleteLead: (id: string) =>
    tenantApi.deleteOne(`/leads/${id}`),

  /** Get kanban data, optionally scoped to one assignee (#701) */
  getKanban: (pipelineId?: string, assignedTo?: string) =>
    tenantApi.getOne<KanbanData>(`/leads/kanban`, {
      params: {
        ...(pipelineId ? { pipeline_id: pipelineId } : {}),
        ...(assignedTo ? { assigned_to: assignedTo } : {}),
      },
    }),

  /** Ownership bucket counts for the All / Mine / Unassigned control (#701) */
  getOwnershipCounts: (params?: LeadOwnershipCountsFilters) =>
    tenantApi.getOne<LeadOwnershipCounts>('/leads/ownership-counts', { params }),

  /** Move lead to different stage */
  /**
   * #717 — a move into a terminal-lost stage must carry its disposition; the
   * backend rejects it with a 422 otherwise.
   */
  moveToStage: (
    id: string,
    stageId: string,
    disposition?: { lost_reason: string; lost_notes?: string },
  ) =>
    tenantApi.postOne<Lead>(`/leads/${id}/move-stage`, {
      stage_id: stageId,
      ...(disposition ?? {}),
    }),

  /** Mark lead as won */
  markAsWon: (id: string, wonData: {
    won_amount: number;
    won_date?: string;
  }) => tenantApi.postOne<Lead>(`/leads/${id}/mark-won`, wonData),

  /** Mark lead as lost */
  markAsLost: (id: string, lostData: {
    lost_reason: string;
    lost_notes?: string;
  }) => tenantApi.postOne<Lead>(`/leads/${id}/mark-lost`, lostData),

  /**
   * #712 — reopen a won/lost deal back into the pipeline's first open stage.
   * Won/lost history stays on the record; the reason lands on the timeline.
   */
  reopen: (id: string, payload?: { reason?: string }) =>
    tenantApi.postOne<Lead>(`/leads/${id}/reopen`, payload ?? {}),

  /** Add a stage change comment */
  addStageComment: (id: string, payload: { stage_id: string; comment?: string }) =>
    tenantApi.postAction(`/leads/${id}/stage-comment`, payload),
};
